import http from "@/shared/services/api-client";
import { adminV1OrganizationsProfilesPartialUpdate } from "@/api/admin-profiles/admin-profiles";
import type { PatchedAdminProfile } from "@/api/model";
import axios from "axios";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";

// The logged-in admin's own profile lives in their home organization, which is
// not necessarily the organization currently being impersonated. Use the
// org-agnostic /profiles/me/ endpoint so self edits always target the admin's
// own profile regardless of impersonation context.
const ME_ENDPOINT = "/api/admin/v1/profiles/me/";

const getDefaultOrganizationId = (): string => {
  const { currentOrganizationId } = useCurrentOrganization();
  return currentOrganizationId.value?.toString() || "";
};

export const downloadAvatar = async (url: string) => {
  try {
    const result = await axios.get(url, {
      responseType: "arraybuffer",
    });
    const base64Image = btoa(
      new Uint8Array(result.data).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      )
    );

    const contentType = result.headers["content-type"];

    return `data:${contentType};base64, ${base64Image}`;
  } catch (e) {
    return { error: (e as any)?.message };
  }
};

export const getUserMe = async () => {
  try {
    const result = await http.get(ME_ENDPOINT);
    return result.data;
  } catch (e) {
    return { error: (e as any)?.message };
  }
};

// Self-service update of the current admin's own profile. Org-agnostic so it
// works correctly even while impersonating another organization.
export const updateMyProfile = async (form: PatchedAdminProfile) => {
  try {
    const result = await http.patch(ME_ENDPOINT, form);
    return result.data;
  } catch (e) {
    return { error: (e as any)?.message };
  }
};

// Update another user's profile within the current (impersonation-aware)
// organization. Used by admin user management, not for editing oneself.
export const updateUserProfile = async (
  userId: number,
  form: PatchedAdminProfile
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesPartialUpdate(
      organizationId,
      userId,
      form
    );
    return result;
  } catch (e) {
    return { error: (e as any)?.message };
  }
};
